/**
 * Attendance QR scan sounds (Web Audio — no sound file).
 * Browsers often suspend AudioContext until a user gesture; call
 * unlockAttendanceScanAudio() from the Start camera click.
 */

let sharedCtx = null;

function getAudioContext() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!sharedCtx || sharedCtx.state === "closed") {
    sharedCtx = new AudioCtx();
  }
  return sharedCtx;
}

async function withRunningContext(play) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    if (ctx.state !== "running") return;

    play(ctx, ctx.currentTime);
  } catch {
    /* ignore autoplay / AudioContext failures */
  }
}

function playTone(ctx, frequency, start, duration, peak) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peak, start + 0.02);
  gain.gain.linearRampToValueAtTime(0, start + duration);
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

/** Call from a user gesture (e.g. Start camera) so later beeps are allowed. */
export async function unlockAttendanceScanAudio() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
  } catch {
    /* ignore */
  }
}

export async function playAttendanceScanBeep() {
  await withRunningContext((ctx, now) => {
    // Ascending chime — same character, much louder for classroom queues.
    playTone(ctx, 784, now, 0.12, 0.28);
    playTone(ctx, 988, now + 0.07, 0.13, 0.32);
    playTone(ctx, 1175, now + 0.14, 0.16, 0.34);
    // Harmonic shimmer on the last note
    playTone(ctx, 1568, now + 0.16, 0.2, 0.16);
  });
}

export async function playAttendanceScanWarningBeep() {
  await withRunningContext((ctx, now) => {
    // Soft descending warning for already-marked / duplicate scans.
    playTone(ctx, 620, now, 0.16, 0.13);
    playTone(ctx, 460, now + 0.14, 0.2, 0.11);
  });
}

export async function playAttendanceScanErrorBeep() {
  await withRunningContext((ctx, now) => {
    // Soft low error buzz for unrecognized / off-roster QR codes.
    playTone(ctx, 320, now, 0.12, 0.12);
    playTone(ctx, 280, now + 0.11, 0.12, 0.11);
    playTone(ctx, 240, now + 0.22, 0.18, 0.1);
  });
}
